/* eslint-disable no-magic-numbers */
/**
 * A simple seeded random number generator using a linear congruential generator (LCG).
 * This ensures deterministic random numbers based on the seed.
 */
export class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  /**
   * Generates a random number between 0 and 1 (exclusive)
   * @returns A pseudo-random number
   */
  public random(): number {
    // Linear Congruential Generator parameters (same as java.util.Random)
    const a = 1103515245;
    const c = 12345;
    const m = 2 ** 31;

    this.seed = (a * this.seed + c) % m;
    return this.seed / m;
  }

  /**
   * Resets the random number generator to the original seed
   * @param newSeed The new seed to use
   */
  public setSeed(newSeed: number): void {
    this.seed = newSeed;
  }
}

/**
 * Creates a numeric seed from a string using a simple hash function.
 * This ensures the same string always produces the same seed.
 * @param str The string to hash
 * @returns A numeric seed
 */
export function stringToSeed(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Ensure positive number
  return Math.abs(hash);
}

/**
 * Creates a numeric seed from multiple strings by combining their hashes.
 * @param strings Array of strings to combine
 * @returns A numeric seed
 */
export function stringsToSeed(...strings: string[]): number {
  const combined = strings.join('|');
  return stringToSeed(combined);
}
